/**
 * Panel de lista de espera (proto agenda-ux-v1 vista 6):
 * FIFO + match explícito + oferta manual. El claim sigue en el portal;
 * la notificación la despacha el backend al pasar a 'offered'.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from '../../shared/modals/Modal';
import {
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { CONTENT_MAX_WIDTH_COMPACT } from '../../shared/layout/breakpoints';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors } from '../../shared/theme/ticketColors';
import { VueltiDurmiendo } from '../../shared/ui/VueltiDurmiendo';
import { agenda, agendaDotColor, agendaFonts, agendaPaperSheet } from '../domain/agendaTheme';
import { minutesToHm } from '../domain/dayBoardLayout';
import type { WaitlistEntryMatch } from '../domain/waitlistMatch';
import type { AppointmentWaitlistEntry } from '../domain/types';

const DAY_SHORT = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'] as const;

function hmOf(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function dayLabel(d: Date): string {
  return `${DAY_SHORT[d.getDay()]} ${d.getDate()}`;
}

function preferenceLabel(e: AppointmentWaitlistEntry): string {
  if (!e.preferredFrom && !e.preferredTo) return 'cualquier horario';
  const from = e.preferredFrom ? new Date(e.preferredFrom) : null;
  const to = e.preferredTo ? new Date(e.preferredTo) : null;
  if (from && to) {
    const sameDay =
      from.getFullYear() === to.getFullYear() &&
      from.getMonth() === to.getMonth() &&
      from.getDate() === to.getDate();
    if (sameDay) return `prefiere ${dayLabel(from)} ${hmOf(e.preferredFrom!)}–${hmOf(e.preferredTo!)}`;
    return `prefiere ${dayLabel(from)} ${hmOf(e.preferredFrom!)} → ${dayLabel(to)} ${hmOf(e.preferredTo!)}`;
  }
  if (from) return `prefiere desde ${dayLabel(from)} ${hmOf(e.preferredFrom!)}`;
  return `prefiere hasta ${dayLabel(to!)} ${hmOf(e.preferredTo!)}`;
}

function waitingLabel(createdAt: string, nowMs: number): string {
  const days = Math.max(0, Math.floor((nowMs - new Date(createdAt).getTime()) / 86_400_000));
  if (days <= 0) return 'desde hoy';
  if (days === 1) return 'hace 1 día';
  return `hace ${days} días`;
}

function matchLabel(m: WaitlistEntryMatch, day: Date): string {
  const sameDay =
    m.startsAt.getFullYear() === day.getFullYear() &&
    m.startsAt.getMonth() === day.getMonth() &&
    m.startsAt.getDate() === day.getDate();
  const hm = minutesToHm(m.startsAt.getHours() * 60 + m.startsAt.getMinutes());
  return sameDay ? `Hueco hoy ${hm}` : `Hueco ${dayLabel(m.startsAt)} ${hm}`;
}

function offeredSlotLabel(e: AppointmentWaitlistEntry): string | null {
  if (!e.offeredStartsAt) return null;
  const d = new Date(e.offeredStartsAt);
  return `${dayLabel(d)} ${hmOf(e.offeredStartsAt)}`;
}

type Props = {
  visible: boolean;
  day: Date;
  entries: AppointmentWaitlistEntry[];
  matches: Map<string, WaitlistEntryMatch>;
  resourceNameById: Map<string, string>;
  resourceColorById: Map<string, string>;
  productNameById: Map<string, string>;
  /** TTL desde Reglas de reserva (booking_waitlist_offer_minutes). */
  offerMinutes: number;
  /** Desde el banner del board: solo entradas con match en esa columna. */
  slotFilter: { resourceId: string; startMinutes: number } | null;
  onClearSlotFilter?: () => void;
  canEdit: boolean;
  offerBusyId?: string | null;
  onOffer: (entry: AppointmentWaitlistEntry, match: WaitlistEntryMatch) => void;
  onClose: () => void;
};

export function AppointmentWaitlistPanel(props: Props) {
  const { height } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [pending, setPending] = useState<{
    entry: AppointmentWaitlistEntry;
    match: WaitlistEntryMatch;
  } | null>(null);

  // El TTL corre mientras el panel está abierto: una oferta puede vencer acá adentro.
  useEffect(() => {
    if (!props.visible) return;
    setNowMs(Date.now());
    const t = setInterval(() => setNowMs(Date.now()), 30_000);
    return () => clearInterval(t);
  }, [props.visible]);

  useEffect(() => {
    if (!props.visible) setPending(null);
  }, [props.visible]);

  // Si un refresh silencioso hace que la entrada deje de estar disponible, suelta el pending.
  useEffect(() => {
    if (!pending) return;
    const still = props.entries.find((e) => e.id === pending.entry.id && e.status === 'waiting');
    if (!still || !props.matches.get(still.id)) setPending(null);
  }, [props.entries, props.matches, pending]);

  const rows = useMemo(() => {
    const list = props.entries.filter((e) => e.status === 'waiting' || e.status === 'offered');
    if (!props.slotFilter) return list;
    const { resourceId } = props.slotFilter;
    return list.filter((e) => props.matches.get(e.id)?.resourceId === resourceId);
  }, [props.entries, props.matches, props.slotFilter]);

  const waitingCount = props.entries.filter((e) => e.status === 'waiting').length;
  const offeredCount = props.entries.filter((e) => e.status === 'offered').length;
  const title =
    waitingCount > 0
      ? `${waitingCount} ${waitingCount === 1 ? 'persona' : 'personas'} en espera`
      : offeredCount > 0
        ? `${offeredCount} con oferta activa`
        : 'Lista de espera';

  return (
    <Modal visible={props.visible} transparent animationType="fade" onRequestClose={props.onClose}>
      <View style={styles.backdrop}>
        <TouchableOpacity
          style={StyleSheet.absoluteFill}
          activeOpacity={1}
          onPress={props.onClose}
          accessible={false}
        />
        <View
          style={[
            styles.card,
            {
              maxHeight: Math.min(620, height * 0.92),
              maxWidth: CONTENT_MAX_WIDTH_COMPACT,
              paddingBottom: Math.max(insets.bottom, spacing.sm),
            },
          ]}
        >
          <View style={styles.header}>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={styles.title}>
                {title}
                {waitingCount > 0 && offeredCount > 0 ? ` · ${offeredCount} con oferta` : ''}
              </Text>
              <Text style={styles.subtitle}>
                Cuando se libera un hueco, el portal ofrece solo. Acá podés ofrecer a mano.
              </Text>
            </View>
            <TouchableOpacity
              style={styles.closeBtn}
              onPress={props.onClose}
              accessibilityRole="button"
              accessibilityLabel="Cerrar"
            >
              <Text style={styles.closeBtnText}>✕</Text>
            </TouchableOpacity>
          </View>

          {props.slotFilter ? (
            <TouchableOpacity
              style={styles.filterChip}
              onPress={props.onClearSlotFilter}
              accessibilityRole="button"
              accessibilityLabel="Quitar filtro de agenda"
            >
              <Text style={styles.filterChipText}>
                {props.resourceNameById.get(props.slotFilter.resourceId) ?? 'Agenda'} · huecos desde{' '}
                {minutesToHm(props.slotFilter.startMinutes)} ✕
              </Text>
            </TouchableOpacity>
          ) : null}

          <ScrollView style={{ flexShrink: 1 }} contentContainerStyle={{ gap: spacing.xs }}>
            {rows.length === 0 ? (
              <View style={styles.empty}>
                <VueltiDurmiendo />
                <Text style={styles.emptyText}>
                  {props.slotFilter
                    ? 'Nadie en espera entra en estos huecos.'
                    : 'Sin nadie en espera.'}
                </Text>
              </View>
            ) : (
              rows.map((e) => {
                const m = props.matches.get(e.id);
                const offered = e.status === 'offered';
                const offerExpired =
                  offered &&
                  e.offerExpiresAt != null &&
                  new Date(e.offerExpiresAt).getTime() <= nowMs;
                const offeredSlot = offered ? offeredSlotLabel(e) : null;
                const offeredResource =
                  offered && e.offeredResourceId
                    ? props.resourceNameById.get(e.offeredResourceId)
                    : null;
                const busy = props.offerBusyId === e.id;
                const offerDisabled =
                  busy || props.offerBusyId != null || pending != null;
                return (
                  <View key={e.id} style={styles.row} testID={`appointments.waitlist.row.${e.id}`}>
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <Text style={styles.rowName} numberOfLines={1}>
                        {e.customerName || e.customerEmail || 'Cliente'}
                      </Text>
                      <Text style={styles.rowMeta} numberOfLines={2}>
                        {props.productNameById.get(e.productId) ?? 'Servicio'}
                        {' · '}
                        {e.resourceId
                          ? props.resourceNameById.get(e.resourceId) ?? 'Agenda'
                          : 'cualquier agenda'}
                        {' · '}
                        {preferenceLabel(e)}
                        {' · '}
                        {waitingLabel(e.createdAt, nowMs)}
                      </Text>
                      {offeredSlot ? (
                        <Text style={styles.rowOfferedMeta} numberOfLines={1}>
                          Oferta: {offeredSlot}
                          {offeredResource ? ` · ${offeredResource}` : ''}
                          {e.offerExpiresAt && !offerExpired
                            ? ` · hasta ${hmOf(e.offerExpiresAt)}`
                            : ''}
                        </Text>
                      ) : null}
                    </View>
                    {offered ? (
                      <View style={offerExpired ? styles.expiredChip : styles.offeredChip}>
                        <Text
                          style={offerExpired ? styles.expiredChipText : styles.offeredChipText}
                        >
                          {offerExpired ? 'Venció' : 'Oferta'}
                        </Text>
                      </View>
                    ) : m ? (
                      <View style={styles.rowRight}>
                        <View
                          style={[
                            styles.matchChip,
                            { borderColor: agendaDotColor(null, 0) },
                          ]}
                        >
                          <View
                            style={[
                              styles.matchDot,
                              {
                                backgroundColor:
                                  props.resourceColorById.get(m.resourceId) ??
                                  agendaDotColor(null, 0),
                              },
                            ]}
                          />
                          <Text style={styles.matchChipText}>
                            {matchLabel(m, props.day)}
                            {props.resourceNameById.get(m.resourceId)
                              ? ` · ${props.resourceNameById.get(m.resourceId)}`
                              : ''}
                          </Text>
                        </View>
                        {props.canEdit ? (
                          <TouchableOpacity
                            style={[styles.offerBtn, offerDisabled && styles.offerBtnDisabled]}
                            disabled={offerDisabled}
                            onPress={() => setPending({ entry: e, match: m })}
                            accessibilityRole="button"
                            accessibilityLabel={`Ofrecer turno a ${e.customerName ?? 'cliente'}`}
                            accessibilityState={{ disabled: offerDisabled }}
                            testID={`appointments.waitlist.offer.${e.id}`}
                          >
                            <Text
                              style={[styles.offerBtnText, offerDisabled && styles.offerBtnTextDisabled]}
                            >
                              {busy ? 'Ofreciendo…' : 'Ofrecer turno'}
                            </Text>
                          </TouchableOpacity>
                        ) : null}
                      </View>
                    ) : (
                      <View style={styles.noMatchChip}>
                        <Text style={styles.noMatchChipText}>Sin hueco aún</Text>
                      </View>
                    )}
                  </View>
                );
              })
            )}
          </ScrollView>

          {pending ? (
            <View style={styles.confirmCard} testID="appointments.waitlist.offerConfirm">
              <Text style={styles.confirmTitle}>¿Ofrecer este hueco?</Text>
              <Text style={styles.confirmBody}>
                {pending.entry.customerName || pending.entry.customerEmail || 'Cliente'} ·{' '}
                {dayLabel(pending.match.startsAt)} {hmOf(pending.match.startsAt.toISOString())} ·{' '}
                {props.resourceNameById.get(pending.match.resourceId) ?? 'Agenda'} · le llega un
                aviso y tiene {props.offerMinutes} min para aceptar.
              </Text>
              <View style={styles.confirmActions}>
                <TouchableOpacity
                  style={styles.confirmCancel}
                  onPress={() => setPending(null)}
                  accessibilityRole="button"
                  accessibilityLabel="Volver"
                  testID="appointments.waitlist.offerConfirm.cancel"
                >
                  <Text style={styles.confirmCancelText}>Volver</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.confirmCta}
                  onPress={() => {
                    const { entry, match } = pending;
                    setPending(null);
                    props.onOffer(entry, match);
                  }}
                  accessibilityRole="button"
                  accessibilityLabel="Confirmar oferta"
                  testID="appointments.waitlist.offerConfirm.ok"
                >
                  <Text style={styles.confirmCtaText}>Ofrecer turno</Text>
                </TouchableOpacity>
              </View>
            </View>
          ) : null}

          <View style={styles.ttlBox}>
            <Text style={styles.ttlText}>
              El cliente tiene {props.offerMinutes} min para aceptar la oferta — el aviso le llega
              solo. El TTL se configura en Reglas de reserva.
            </Text>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: ticketColors.dim,
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.md,
  },
  card: {
    ...agendaPaperSheet,
    width: '100%',
    padding: spacing.lg,
    gap: spacing.sm,
  },
  header: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm },
  title: {
    fontSize: 17,
    fontWeight: '800',
    color: agenda.ink,
    fontFamily: agendaFonts.sans,
  },
  subtitle: {
    marginTop: 2,
    fontSize: 12.5,
    color: agenda.ink2,
    fontFamily: agendaFonts.sans,
  },
  closeBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: agenda.surface2,
  },
  closeBtnText: { fontSize: 13, color: agenda.ink2, fontFamily: agendaFonts.sans },
  filterChip: {
    alignSelf: 'flex-start',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 999,
    backgroundColor: agenda.chooseInk,
  },
  filterChipText: {
    fontSize: 12,
    fontWeight: '700',
    color: ticketColors.goldInk,
    fontFamily: agendaFonts.sans,
  },
  empty: { alignItems: 'center', justifyContent: 'center', padding: 24, gap: spacing.xs },
  emptyText: {
    fontSize: 14,
    color: agenda.ink2,
    fontFamily: agendaFonts.sans,
    textAlign: 'center',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: agenda.border,
    backgroundColor: agenda.surface,
  },
  rowName: {
    fontSize: 14,
    fontWeight: '700',
    color: agenda.ink,
    fontFamily: agendaFonts.sans,
  },
  rowMeta: {
    marginTop: 2,
    fontSize: 12,
    color: agenda.ink3,
    fontFamily: agendaFonts.sans,
  },
  rowOfferedMeta: {
    marginTop: 3,
    fontSize: 12,
    fontWeight: '600',
    color: agenda.brand,
    fontFamily: agendaFonts.sans,
  },
  rowRight: { alignItems: 'flex-end', gap: 6 },
  matchChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 9,
    paddingVertical: 4,
    borderRadius: 999,
    borderWidth: 1,
    backgroundColor: agenda.surface2,
  },
  matchDot: { width: 7, height: 7, borderRadius: 4 },
  matchChipText: {
    fontSize: 11.5,
    fontWeight: '700',
    color: agenda.ink,
    fontFamily: agendaFonts.sans,
  },
  offerBtn: {
    minHeight: 36,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 9,
    backgroundColor: agenda.brand,
    justifyContent: 'center',
  },
  offerBtnDisabled: {
    backgroundColor: agenda.surface2,
  },
  offerBtnText: {
    fontSize: 12.5,
    fontWeight: '800',
    color: ticketColors.goldInk,
    fontFamily: agendaFonts.sans,
  },
  offerBtnTextDisabled: {
    color: agenda.ink3,
    fontWeight: '700',
  },
  offeredChip: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 999,
    backgroundColor: ticketColors.warnBg,
    borderWidth: 1,
    borderColor: ticketColors.warnBorder,
  },
  offeredChipText: {
    fontSize: 11.5,
    fontWeight: '700',
    color: ticketColors.warn,
    fontFamily: agendaFonts.sans,
  },
  expiredChip: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 999,
    backgroundColor: agenda.surface2,
    borderWidth: 1,
    borderColor: agenda.border,
  },
  expiredChipText: {
    fontSize: 11.5,
    fontWeight: '700',
    color: agenda.ink3,
    fontFamily: agendaFonts.sans,
  },
  noMatchChip: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 999,
    backgroundColor: agenda.surface2,
  },
  noMatchChipText: {
    fontSize: 11.5,
    fontWeight: '600',
    color: agenda.ink3,
    fontFamily: agendaFonts.sans,
  },
  confirmCard: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: ticketColors.warnBorder,
    backgroundColor: ticketColors.warnBg,
    padding: spacing.sm,
    gap: spacing.xs,
  },
  confirmTitle: {
    fontSize: 13.5,
    fontWeight: '800',
    color: agenda.ink,
    fontFamily: agendaFonts.sans,
  },
  confirmBody: {
    fontSize: 12.5,
    color: agenda.ink2,
    lineHeight: 18,
    fontFamily: agendaFonts.sans,
  },
  confirmActions: {
    flexDirection: 'row',
    gap: spacing.xs,
    marginTop: 2,
  },
  confirmCancel: {
    minHeight: 36,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 9,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    backgroundColor: ticketColors.paper,
    justifyContent: 'center',
  },
  confirmCancelText: {
    fontSize: 12.5,
    fontWeight: '700',
    color: agenda.ink2,
    fontFamily: agendaFonts.sans,
  },
  confirmCta: {
    flex: 1,
    minHeight: 36,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 9,
    backgroundColor: agenda.brand,
    alignItems: 'center',
    justifyContent: 'center',
  },
  confirmCtaText: {
    fontSize: 12.5,
    fontWeight: '800',
    color: ticketColors.goldInk,
    fontFamily: agendaFonts.sans,
  },
  ttlBox: {
    marginTop: spacing.xs,
    padding: 10,
    borderRadius: 10,
    backgroundColor: agenda.surface2,
  },
  ttlText: { fontSize: 12, color: agenda.ink2, fontFamily: agendaFonts.sans, lineHeight: 17 },
});
